<script setup lang="ts">
import {Slider} from "primevue";
import {computed, onBeforeMount, reactive, useId, watch} from "vue";
import {useDebounceFn} from "@vueuse/core";
import {judgmentOptions, kdm, editMode, discussionState, getCommentIcon} from "@/store/kdm";
import {SliderUpdate} from "#/KdmNode";

const props = defineProps({
  cellId: String,
  indicatorId: null,
  index: Number,
  label: String,
  moderationClass: String,
  href: String,
  isInKik: Boolean,
  isLocked: Boolean
})

const emit = defineEmits<{
  update: [value: SliderUpdate],
  delete: [value: string]
}>()

const id = useId()

const data = reactive({
  sliderValue: 0,
  judgmentId: '',
})

// Red-flag wording follows the global mode (crosslinks vs context); "link" is
// avoided (too close to Crosslink).
const kikText = computed(() => (kdm.value as any)?.use_crosslinks_for_ipr
  ? 'Indicator/Argument no longer crosslinked to the KIK'
  : 'Indicator/Argument no longer contextualized in the KIK');

const bgColor = computed(() => {
  if (data.judgmentId) {
    const judgment = judgmentOptions.value.get(data.judgmentId)
    if (judgment) {
      return `var(--judgment-option-${data.judgmentId})`
    }
  }
  return 'white';
})

// Helper to get contrasting text color based on background
const getTextColor = (bgColor: string): string => {
  if (!bgColor || bgColor === '#FFFFFF' || bgColor === 'transparent') return '#000000';
  const hex = bgColor.replace('#', '');
  if (hex.length !== 6) return '#000000';
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

const commentIcon = computed(() => getCommentIcon(`${props.cellId}-${props.indicatorId}`))

function removeIndicator(): void {
  emit('delete', props.indicatorId)
}

function openDiscussion(): void {
  discussionState.coords = `${props.cellId}-${props.indicatorId}`;
  discussionState.nodeId = kdm.value?.nid || '';
  // Can write if NOT locked (assigned or admin)
  discussionState.canWrite = !props.isLocked; 
  discussionState.visible = true;
}

onBeforeMount(() => {
  const matrixData = kdm.value?.field_matrix_data
  if (typeof matrixData === 'string') {
    let matrixDataObject = {}
    try { matrixDataObject = JSON.parse(matrixData) } catch(e) {}
    
    if (matrixDataObject && Object.hasOwn(matrixDataObject, props.cellId || '')) {
      const cellDataFromServer = matrixDataObject[props.cellId || '']
      if (cellDataFromServer && Object.hasOwn(cellDataFromServer, props.indicatorId)) {
        const item = cellDataFromServer[props.indicatorId];
        if (item) {
            data.sliderValue = item.sliderValue || 0
            data.judgmentId = item.judgmentId || ''
        }
      }
    }
  }
  watch(data, useDebounceFn(() => {
    emit('update', {index: props.index || 0, indicatorId: props.indicatorId, ...data})
  }, 1000))
})
</script>

<template>
  <div class="kdm__slider-row" :title="isInKik === false ? kikText : ''">
    <div class="d-flex align-items-center justify-content-between w-100 mb-1">
      <div class="d-flex align-items-center gap-2">
        <label :for="id" class="small">
          <a :href="href || `/node/${indicatorId}`" target="_blank" :class="[moderationClass, isInKik === false ? 'fw-bold' : '']">{{ label }}</a>
        </label>
        <button 
          type="button" 
          class="btn btn-sm btn-link text-decoration-none p-0 border-0"
          @click.stop="openDiscussion"
          title="Discuss this element"
        >
          <i class="bi" :class="[commentIcon.icon, commentIcon.class]" style="font-size: 0.9rem;"></i>
        </button>
      </div>
      <button 
        v-if="editMode && !isLocked"
        type="button" 
        class="btn btn-sm btn-danger py-0 px-2"
        @click.stop="removeIndicator"
        title="Remove indicator from this cell"
      >
        <span>×</span>
      </button>
    </div>
    <div class="d-flex align-items-center gap-2 mb-2">
      <select :disabled="!editMode || isLocked" v-model="data.judgmentId" :id="id" class="form-select form-select-sm py-0" :style="`background-color: ${bgColor}; flex: 1;`">
        <template v-for="[pid,judgment] in judgmentOptions" :key="pid">
          <option
            :value="pid"
            :style="{
              backgroundColor: judgment.color,
              color: getTextColor(judgment.color)
            }"
          >{{ judgment.title }}</option>
        </template>
      </select>
      <input
        type="number"
        v-model.number="data.sliderValue"
        :disabled="!editMode || isLocked"
        class="form-control form-control-sm text-center"
        style="width: 60px;"
        min="0"
        max="100"
      />
    </div>
    <Slider v-if="editMode" :disabled="isLocked" v-model:modelValue="data.sliderValue" class="mb-3"/>
  </div>
</template>

<style scoped>
.kdm__slider-row {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
  margin-bottom: 0.5rem;
}

.kdm__slider-row label {
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
}

.kdm__slider-row label a {
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.btn-danger {
  font-size: 1.25rem;
  line-height: 1;
}
</style>